import { getStandardizedLocationRedirect, normalizeCitySlug } from "./locationRoutes";
import { getLocationBySlug, isLocationQualified, getStateBySlug, getCountryBySlug } from "./locationRegistry";
import { getSeoEntryByRoute } from "./seoRegistry";
import { generateBreadcrumbJsonLd } from "./breadcrumbRegistry";
import { isSeoIndexEligible } from "./seoEligibility";
import { getOrganizationSchema, getSoftwareApplicationSchema, getWebSiteSchema } from "./bizNearYouEntity";
import { getNicheIntentBySlug } from "./nicheIntentRegistry";
import { sanitizeDuplicateWords } from "./jobCategoryRegistry";
import { generateTier1StateMetadata, generateTier2CityMetadata, generateTier3NicheMetadata } from "./seoProgrammaticEngine";
import { parseRouteLanguage } from "./languageRegistry";

export interface SeoPageMetadata {
  title: string;
  description: string;
  canonicalUrl: string;
  robots?: string; // e.g. "index, follow" or "noindex, follow" or "noindex, nofollow"
  ogTitle?: string;
  ogDescription?: string;
  ogUrl?: string;
  ogImage?: string;
  twitterCard?: string;
  schemaJson?: object;
  datePublished?: string;
  dateModified?: string;
}

export const seoConfig: Record<string, SeoPageMetadata> = {
  "/": {
    title: "Find Local Businesses That May Be Hiring Near You | BizNearYou",
    description: "Search nearby businesses, review recent hiring signals, and find employers you may want to contact directly—even when they have not posted a job on a major job board.",
    canonicalUrl: "https://getbiznearyou.com/",
    robots: "index, follow",
    schemaJson: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebSite",
          "@id": "https://getbiznearyou.com/#website",
          "name": "BizNearYou",
          "url": "https://getbiznearyou.com/",
          "description": "Discover nearby businesses worth contacting for jobs and internships. Compare public hiring signals, save opportunities, and organize your outreach."
        },
        {
          "@type": "SoftwareApplication",
          "@id": "https://getbiznearyou.com/#app",
          "name": "BizNearYou",
          "image": "https://getbiznearyou.com/favicon-512.png",
          "url": "https://getbiznearyou.com/",
          "operatingSystem": "All",
          "applicationCategory": "BusinessApplication",
          "description": "A proactive local business search platform and outreach CRM for students, freelancers, and early-career job seekers to identify and pitch businesses worth contacting."
        },
        {
          "@type": "Organization",
          "@id": "https://getbiznearyou.com/#organization",
          "name": "BizNearYou",
          "url": "https://getbiznearyou.com/",
          "logo": "https://getbiznearyou.com/favicon-512.png",
          "description": "BizNearYou helps job seekers discover nearby businesses that may be worth contacting for jobs or internships."
        },
        {
          "@type": "FAQPage",
          "@id": "https://getbiznearyou.com/#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What is BizNearYou?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "BizNearYou is a local employer discovery engine and job search CRM. Instead of displaying only paid job ads, BizNearYou maps 100% of verified local commercial businesses in your commute radius, evaluates public growth indicators, and helps you pitch decision makers directly."
              }
            },
            {
              "@type": "Question",
              "name": "How does BizNearYou differ from traditional job boards like Indeed or LinkedIn?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Traditional job boards list only companies that pay expensive monthly advertising fees ($300-$1,000+). Many local small businesses hire through direct inquiry rather than posting ads. BizNearYou indexes physical businesses across 100+ categories regardless of ad budget, unlocking unposted local job opportunities."
              }
            },
            {
              "@type": "Question",
              "name": "What are Hiring Signals?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Hiring Signals are public commercial metrics—such as recent customer review spikes, new branch locations, website service expansions, and operational updates—that indicate a business is growing and likely expanding its team."
              }
            },
            {
              "@type": "Question",
              "name": "What is an Opportunity Score?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "An Opportunity Score is a 1-100 composite rating evaluating an employer's contactability, website quality, business stability, and growth trajectory to help job seekers prioritize high-yield outreach targets."
              }
            }
          ]
        }
      ]
    }
  },
  "/find-jobs": {
    title: "Find Local Jobs and Nearby Employers | BizNearYou",
    description: "Search nearby businesses, compare public hiring signals, and organize direct outreach to local employers from one workspace.",
    canonicalUrl: "https://getbiznearyou.com/find-jobs",
    robots: "index, follow",
    schemaJson: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Find Local Jobs and Nearby Employers",
      "description": "Search nearby businesses, compare public hiring signals, and organize direct outreach to local employers from one workspace.",
      "url": "https://getbiznearyou.com/find-jobs"
    }
  },
  "/hidden-jobs": {
    title: "How to Find Hidden and Unadvertised Jobs | BizNearYou",
    description: "Learn how to discover local job opportunities before they appear on major job boards and how to contact employers respectfully.",
    canonicalUrl: "https://getbiznearyou.com/hidden-jobs",
    robots: "index, follow",
    schemaJson: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Article",
          "@id": "https://getbiznearyou.com/hidden-jobs#article",
          "isPartOf": {
            "@type": "WebPage",
            "@id": "https://getbiznearyou.com/hidden-jobs",
            "url": "https://getbiznearyou.com/hidden-jobs",
            "name": "How to Find Hidden and Unadvertised Jobs | BizNearYou"
          },
          "headline": "How to Find Hidden and Unadvertised Jobs",
          "description": "Learn how to discover local job opportunities before they appear on major job boards and how to contact employers respectfully.",
          "inLanguage": "en",
          "mainEntityOfPage": "https://getbiznearyou.com/hidden-jobs",
          "author": {
            "@type": "Organization",
            "name": "BizNearYou",
            "url": "https://getbiznearyou.com/"
          },
          "publisher": {
            "@type": "Organization",
            "name": "BizNearYou",
            "logo": {
              "@type": "ImageObject",
              "url": "https://getbiznearyou.com/favicon-512.png"
            }
          },
          "image": {
            "@type": "ImageObject",
            "url": "https://getbiznearyou.com/favicon-512.png"
          },
          "datePublished": "2026-03-10T09:00:00+00:00",
          "dateModified": "2026-07-15T12:00:00+00:00"
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://getbiznearyou.com/hidden-jobs#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://getbiznearyou.com/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Hidden Jobs",
              "item": "https://getbiznearyou.com/hidden-jobs"
            }
          ]
        }
      ]
    }
  },
  "/local-internships": {
    title: "Find Local Internships Near You | BizNearYou",
    description: "Discover nearby organizations that may offer internships and learn how to contact them even when no internship is publicly listed.",
    canonicalUrl: "https://getbiznearyou.com/local-internships",
    robots: "index, follow",
    schemaJson: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Article",
          "@id": "https://getbiznearyou.com/local-internships#article",
          "isPartOf": {
            "@type": "WebPage",
            "@id": "https://getbiznearyou.com/local-internships",
            "url": "https://getbiznearyou.com/local-internships",
            "name": "Find Local Internships Near You | BizNearYou"
          },
          "headline": "Find Local Internships Near You",
          "description": "Discover nearby organizations that may offer internships and learn how to contact them even when no internship is publicly listed.",
          "inLanguage": "en",
          "mainEntityOfPage": "https://getbiznearyou.com/local-internships",
          "author": {
            "@type": "Organization",
            "name": "BizNearYou",
            "url": "https://getbiznearyou.com/"
          },
          "publisher": {
            "@type": "Organization",
            "name": "BizNearYou",
            "logo": {
              "@type": "ImageObject",
              "url": "https://getbiznearyou.com/favicon-512.png"
            }
          },
          "image": {
            "@type": "ImageObject",
            "url": "https://getbiznearyou.com/favicon-512.png"
          },
          "datePublished": "2026-03-12T09:00:00+00:00",
          "dateModified": "2026-07-15T12:00:00+00:00"
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://getbiznearyou.com/local-internships#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://getbiznearyou.com/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Local Internships",
              "item": "https://getbiznearyou.com/local-internships"
            }
          ]
        }
      ]
    }
  },
  "/first-jobs": {
    title: "Find a First Job Without Experience | BizNearYou",
    description: "Find nearby businesses to contact, compare promising opportunities, and organize your first job search without relying only on job boards.",
    canonicalUrl: "https://getbiznearyou.com/first-jobs",
    robots: "index, follow",
    schemaJson: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Article",
          "@id": "https://getbiznearyou.com/first-jobs#article",
          "isPartOf": {
            "@type": "WebPage",
            "@id": "https://getbiznearyou.com/first-jobs",
            "url": "https://getbiznearyou.com/first-jobs",
            "name": "Find a First Job Without Experience | BizNearYou"
          },
          "headline": "Find a First Job Without Experience",
          "description": "Find nearby businesses to contact, compare promising opportunities, and organize your first job search without relying only on job boards.",
          "inLanguage": "en",
          "mainEntityOfPage": "https://getbiznearyou.com/first-jobs",
          "author": {
            "@type": "Organization",
            "name": "BizNearYou",
            "url": "https://getbiznearyou.com/"
          },
          "publisher": {
            "@type": "Organization",
            "name": "BizNearYou",
            "logo": {
              "@type": "ImageObject",
              "url": "https://getbiznearyou.com/favicon-512.png"
            }
          },
          "image": {
            "@type": "ImageObject",
            "url": "https://getbiznearyou.com/favicon-512.png"
          },
          "datePublished": "2026-03-15T09:00:00+00:00",
          "dateModified": "2026-07-15T12:00:00+00:00"
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://getbiznearyou.com/first-jobs#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://getbiznearyou.com/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "First Jobs",
              "item": "https://getbiznearyou.com/first-jobs"
            }
          ]
        }
      ]
    }
  },
  "/businesses-hiring-near-me": {
    title: "Find Businesses That May Be Hiring Near You | BizNearYou",
    description: "Research nearby businesses and review public signals that may help you decide which employers are worth contacting first.",
    canonicalUrl: "https://getbiznearyou.com/businesses-hiring-near-me",
    robots: "index, follow",
    schemaJson: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Article",
          "@id": "https://getbiznearyou.com/businesses-hiring-near-me#article",
          "isPartOf": {
            "@type": "WebPage",
            "@id": "https://getbiznearyou.com/businesses-hiring-near-me",
            "url": "https://getbiznearyou.com/businesses-hiring-near-me",
            "name": "Find Businesses That May Be Hiring Near You | BizNearYou"
          },
          "headline": "Find Businesses That May Be Hiring Near You",
          "description": "Research nearby businesses and review public signals that may help you decide which employers are worth contacting first.",
          "inLanguage": "en",
          "mainEntityOfPage": "https://getbiznearyou.com/businesses-hiring-near-me",
          "author": {
            "@type": "Organization",
            "name": "BizNearYou",
            "url": "https://getbiznearyou.com/"
          },
          "publisher": {
            "@type": "Organization",
            "name": "BizNearYou",
            "logo": {
              "@type": "ImageObject",
              "url": "https://getbiznearyou.com/favicon-512.png"
            }
          },
          "image": {
            "@type": "ImageObject",
            "url": "https://getbiznearyou.com/favicon-512.png"
          },
          "datePublished": "2026-03-18T09:00:00+00:00",
          "dateModified": "2026-07-15T12:00:00+00:00"
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://getbiznearyou.com/businesses-hiring-near-me#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://getbiznearyou.com/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Businesses Hiring Near Me",
              "item": "https://getbiznearyou.com/businesses-hiring-near-me"
            }
          ]
        }
      ]
    }
  },
  "/how-it-works": {
    title: "How BizNearYou Finds Local Job Opportunities",
    description: "See how BizNearYou searches nearby businesses, evaluates public signals, organizes opportunities, and supports direct job outreach.",
    canonicalUrl: "https://getbiznearyou.com/how-it-works",
    robots: "index, follow",
    schemaJson: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://getbiznearyou.com/how-it-works",
          "url": "https://getbiznearyou.com/how-it-works",
          "name": "How BizNearYou Finds Local Job Opportunities",
          "description": "See how BizNearYou searches nearby businesses, evaluates public signals, organizes opportunities, and supports direct job outreach."
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://getbiznearyou.com/how-it-works#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://getbiznearyou.com/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "How It Works",
              "item": "https://getbiznearyou.com/how-it-works"
            }
          ]
        }
      ]
    }
  },
  "/how-to-contact-local-businesses-for-a-job": {
    title: "How to Contact Local Businesses About a Job",
    description: "Learn how to email, call, or visit a local business about employment, including practical scripts and respectful follow-up advice.",
    canonicalUrl: "https://getbiznearyou.com/how-to-contact-local-businesses-for-a-job",
    robots: "index, follow",
    schemaJson: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Article",
          "@id": "https://getbiznearyou.com/how-to-contact-local-businesses-for-a-job#article",
          "isPartOf": {
            "@type": "WebPage",
            "@id": "https://getbiznearyou.com/how-to-contact-local-businesses-for-a-job",
            "url": "https://getbiznearyou.com/how-to-contact-local-businesses-for-a-job",
            "name": "How to Contact Local Businesses About a Job | BizNearYou"
          },
          "headline": "How to Contact Local Businesses About a Job",
          "description": "Learn how to email, call, or visit a local business about employment, including practical scripts and respectful follow-up advice.",
          "inLanguage": "en",
          "mainEntityOfPage": "https://getbiznearyou.com/how-to-contact-local-businesses-for-a-job",
          "author": {
            "@type": "Organization",
            "name": "BizNearYou",
            "url": "https://getbiznearyou.com/"
          },
          "publisher": {
            "@type": "Organization",
            "name": "BizNearYou",
            "logo": {
              "@type": "ImageObject",
              "url": "https://getbiznearyou.com/favicon-512.png"
            }
          },
          "image": {
            "@type": "ImageObject",
            "url": "https://getbiznearyou.com/favicon-512.png"
          },
          "datePublished": "2026-03-20T09:00:00+00:00",
          "dateModified": "2026-07-15T12:00:00+00:00"
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://getbiznearyou.com/how-to-contact-local-businesses-for-a-job#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://getbiznearyou.com/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Contact Guide",
              "item": "https://getbiznearyou.com/how-to-contact-local-businesses-for-a-job"
            }
          ]
        }
      ]
    }
  },
  "/how-to-prioritize-businesses": {
    title: "How to Decide Which Employers to Contact First",
    description: "Compare nearby employers using relevance, public activity, distance, workplace information, and other useful job-search signals.",
    canonicalUrl: "https://getbiznearyou.com/how-to-prioritize-businesses",
    robots: "index, follow",
    schemaJson: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Article",
          "@id": "https://getbiznearyou.com/how-to-prioritize-businesses#article",
          "isPartOf": {
            "@type": "WebPage",
            "@id": "https://getbiznearyou.com/how-to-prioritize-businesses",
            "url": "https://getbiznearyou.com/how-to-prioritize-businesses",
            "name": "How to Decide Which Employers to Contact First | BizNearYou"
          },
          "headline": "How to Decide Which Employers to Contact First",
          "description": "Compare nearby employers using relevance, public activity, distance, workplace information, and other useful job-search signals.",
          "inLanguage": "en",
          "mainEntityOfPage": "https://getbiznearyou.com/how-to-prioritize-businesses",
          "author": {
            "@type": "Organization",
            "name": "BizNearYou",
            "url": "https://getbiznearyou.com/"
          },
          "publisher": {
            "@type": "Organization",
            "name": "BizNearYou",
            "logo": {
              "@type": "ImageObject",
              "url": "https://getbiznearyou.com/favicon-512.png"
            }
          },
          "image": {
            "@type": "ImageObject",
            "url": "https://getbiznearyou.com/favicon-512.png"
          },
          "datePublished": "2026-03-22T09:00:00+00:00",
          "dateModified": "2026-07-15T12:00:00+00:00"
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://getbiznearyou.com/how-to-prioritize-businesses#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://getbiznearyou.com/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Prioritizing Guide",
              "item": "https://getbiznearyou.com/how-to-prioritize-businesses"
            }
          ]
        }
      ]
    }
  },
  "/how-to-organize-local-job-search": {
    title: "How to Organize Your Local Job Search",
    description: "Track employers, outreach, applications, responses, and follow-ups without losing promising local opportunities.",
    canonicalUrl: "https://getbiznearyou.com/how-to-organize-local-job-search",
    robots: "index, follow",
    schemaJson: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Article",
          "@id": "https://getbiznearyou.com/how-to-organize-local-job-search#article",
          "isPartOf": {
            "@type": "WebPage",
            "@id": "https://getbiznearyou.com/how-to-organize-local-job-search",
            "url": "https://getbiznearyou.com/how-to-organize-local-job-search",
            "name": "How to Organize Your Local Job Search | BizNearYou"
          },
          "headline": "How to Organize Your Local Job Search",
          "description": "Track employers, outreach, applications, responses, and follow-ups without losing promising local opportunities.",
          "inLanguage": "en",
          "mainEntityOfPage": "https://getbiznearyou.com/how-to-organize-local-job-search",
          "author": {
            "@type": "Organization",
            "name": "BizNearYou",
            "url": "https://getbiznearyou.com/"
          },
          "publisher": {
            "@type": "Organization",
            "name": "BizNearYou",
            "logo": {
              "@type": "ImageObject",
              "url": "https://getbiznearyou.com/favicon-512.png"
            }
          },
          "image": {
            "@type": "ImageObject",
            "url": "https://getbiznearyou.com/favicon-512.png"
          },
          "datePublished": "2026-03-24T09:00:00+00:00",
          "dateModified": "2026-07-15T12:00:00+00:00"
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://getbiznearyou.com/how-to-organize-local-job-search#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://getbiznearyou.com/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Organization Guide",
              "item": "https://getbiznearyou.com/how-to-organize-local-job-search"
            }
          ]
        }
      ]
    }
  },
  "/how-to-ask-if-hiring": {
    title: "How to Ask a Business if They Are Hiring",
    description: "Use respectful email, phone, and in-person scripts to ask local businesses about current or future job opportunities.",
    canonicalUrl: "https://getbiznearyou.com/how-to-ask-if-hiring",
    robots: "index, follow",
    schemaJson: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Article",
          "@id": "https://getbiznearyou.com/how-to-ask-if-hiring#article",
          "isPartOf": {
            "@type": "WebPage",
            "@id": "https://getbiznearyou.com/how-to-ask-if-hiring",
            "url": "https://getbiznearyou.com/how-to-ask-if-hiring",
            "name": "How to Ask a Business if They Are Hiring | BizNearYou"
          },
          "headline": "How to Ask a Business if They Are Hiring",
          "description": "Use respectful email, phone, and in-person scripts to ask local businesses about current or future job opportunities.",
          "inLanguage": "en",
          "mainEntityOfPage": "https://getbiznearyou.com/how-to-ask-if-hiring",
          "author": {
            "@type": "Organization",
            "name": "BizNearYou",
            "url": "https://getbiznearyou.com/"
          },
          "publisher": {
            "@type": "Organization",
            "name": "BizNearYou",
            "logo": {
              "@type": "ImageObject",
              "url": "https://getbiznearyou.com/favicon-512.png"
            }
          },
          "image": {
            "@type": "ImageObject",
            "url": "https://getbiznearyou.com/favicon-512.png"
          },
          "datePublished": "2026-03-26T09:00:00+00:00",
          "dateModified": "2026-07-15T12:00:00+00:00"
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://getbiznearyou.com/how-to-ask-if-hiring#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://getbiznearyou.com/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Outreach Script Guide",
              "item": "https://getbiznearyou.com/how-to-ask-if-hiring"
            }
          ]
        }
      ]
    }
  },
  "/hiring-signals-explained": {
    title: "Local Business Hiring Signals Explained",
    description: "Understand the public signals BizNearYou uses, what they may suggest, and why no signal alone confirms that a business is hiring.",
    canonicalUrl: "https://getbiznearyou.com/hiring-signals-explained",
    robots: "index, follow",
    schemaJson: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Article",
          "@id": "https://getbiznearyou.com/hiring-signals-explained#article",
          "isPartOf": {
            "@type": "WebPage",
            "@id": "https://getbiznearyou.com/hiring-signals-explained",
            "url": "https://getbiznearyou.com/hiring-signals-explained",
            "name": "Local Business Hiring Signals Explained | BizNearYou"
          },
          "headline": "Local Business Hiring Signals Explained",
          "description": "Understand the public signals BizNearYou uses, what they may suggest, and why no signal alone confirms that a business is hiring.",
          "inLanguage": "en",
          "mainEntityOfPage": "https://getbiznearyou.com/hiring-signals-explained",
          "author": {
            "@type": "Organization",
            "name": "BizNearYou",
            "url": "https://getbiznearyou.com/"
          },
          "publisher": {
            "@type": "Organization",
            "name": "BizNearYou",
            "logo": {
              "@type": "ImageObject",
              "url": "https://getbiznearyou.com/favicon-512.png"
            }
          },
          "image": {
            "@type": "ImageObject",
            "url": "https://getbiznearyou.com/favicon-512.png"
          },
          "datePublished": "2026-03-28T09:00:00+00:00",
          "dateModified": "2026-07-15T12:00:00+00:00"
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://getbiznearyou.com/hiring-signals-explained#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://getbiznearyou.com/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Hiring Signals Explained",
              "item": "https://getbiznearyou.com/hiring-signals-explained"
            }
          ]
        }
      ]
    }
  },
  "/docs": {
    title: "BizNearYou Help, Documentation and FAQ",
    description: "Learn how to search businesses, compare opportunities, save leads, organize outreach, manage your account, and use BizNearYou effectively.",
    canonicalUrl: "https://getbiznearyou.com/docs",
    robots: "index, follow",
    schemaJson: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://getbiznearyou.com/docs",
          "url": "https://getbiznearyou.com/docs",
          "name": "BizNearYou Help, Documentation and FAQ",
          "description": "Learn how to search businesses, compare opportunities, save leads, organize outreach, manage your account, and use BizNearYou effectively."
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://getbiznearyou.com/docs#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://getbiznearyou.com/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Documentation",
              "item": "https://getbiznearyou.com/docs"
            }
          ]
        }
      ]
    }
  },
  "/faq": {
    title: "Frequently Asked Questions (FAQ) | BizNearYou",
    description: "Get factual answers about BizNearYou local business discovery, Opportunity Scores, Hiring Signals, data collection, and direct employer outreach.",
    canonicalUrl: "https://getbiznearyou.com/faq",
    robots: "index, follow",
    schemaJson: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "FAQPage",
          "@id": "https://getbiznearyou.com/faq#faq",
          "url": "https://getbiznearyou.com/faq",
          "name": "BizNearYou Frequently Asked Questions",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What is BizNearYou?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "BizNearYou is a local employer discovery engine and proactive job search CRM. It maps real physical businesses in your commute radius and gives job seekers direct access to unposted job opportunities."
              }
            },
            {
              "@type": "Question",
              "name": "How are Opportunity Scores calculated?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Opportunity Scores (1-100) are generated by evaluating phone validity, domain email availability, review count stability, website operational status, and business age."
              }
            },
            {
              "@type": "Question",
              "name": "Where does BizNearYou get its business data?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "BizNearYou aggregates public commercial information from official Google Maps Platform APIs, official state Secretary of State business registries, public web domain records, and public review platforms."
              }
            },
            {
              "@type": "Question",
              "name": "Does BizNearYou sell user data or contact lists?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "No. BizNearYou never sells user personal data, resume information, or search history to third parties."
              }
            }
          ]
        }
      ]
    }
  },
  "/why-biznearyou": {
    title: "Why BizNearYou - Proactive Local Business Discovery",
    description: "Discover why job seekers, trade specialists, students, and career switchers choose BizNearYou over national ad boards.",
    canonicalUrl: "https://getbiznearyou.com/why-biznearyou",
    robots: "index, follow",
    schemaJson: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://getbiznearyou.com/why-biznearyou",
          "url": "https://getbiznearyou.com/why-biznearyou",
          "name": "Why Choose BizNearYou for Local Job Discovery",
          "description": "Discover why job seekers choose BizNearYou over traditional paid job boards."
        },
        {
          "@type": "FAQPage",
          "@id": "https://getbiznearyou.com/why-biznearyou#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "Why search local businesses before job ads are posted?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Over 70% of local small business hiring happens before a formal job posting is published. Reaching out directly allows candidates to speak with owners before hundreds of applicants flood an online portal."
              }
            },
            {
              "@type": "Question",
              "name": "Who is BizNearYou designed for?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "BizNearYou is designed for job seekers seeking local employment, trade workers targeting local contractors, students looking for nearby internships, and career switchers wanting short commutes."
              }
            }
          ]
        }
      ]
    }
  },
  "/how-data-is-collected": {
    title: "How Business Data Is Collected & Verified | BizNearYou",
    description: "Transparent breakdown of public APIs, state business filings, domain WHOIS records, and verification safeguards powering BizNearYou.",
    canonicalUrl: "https://getbiznearyou.com/how-data-is-collected",
    robots: "index, follow",
    schemaJson: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "TechArticle",
          "@id": "https://getbiznearyou.com/how-data-is-collected#article",
          "name": "How Business Information Is Collected and Verified",
          "description": "Detailed explanation of public data sources and validation algorithms used by BizNearYou.",
          "inLanguage": "en"
        },
        {
          "@type": "FAQPage",
          "@id": "https://getbiznearyou.com/how-data-is-collected#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "Where does BizNearYou obtain business information?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Data is sourced from official Google Maps Platform Places APIs, Secretary of State corporate registration portals, public domain WHOIS records, and verified business web pages."
              }
            },
            {
              "@type": "Question",
              "name": "How can a business owner request corrections or removal?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Business owners can submit a removal or update request via our Contact page, and records are updated or suppressed within 24-48 hours."
              }
            }
          ]
        }
      ]
    }
  },
  "/how-scores-are-calculated": {
    title: "How Opportunity Scores Are Calculated | BizNearYou",
    description: "Learn the mathematical 1-100 rating algorithm evaluating local employer contactability, business longevity, website health, and review stability.",
    canonicalUrl: "https://getbiznearyou.com/how-scores-are-calculated",
    robots: "index, follow",
    schemaJson: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "TechArticle",
          "@id": "https://getbiznearyou.com/how-scores-are-calculated#article",
          "name": "How Opportunity Scores Are Calculated",
          "description": "Comprehensive explanation of the 1-100 Opportunity Score algorithm.",
          "inLanguage": "en"
        },
        {
          "@type": "FAQPage",
          "@id": "https://getbiznearyou.com/how-scores-are-calculated#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What factors determine an Opportunity Score?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "The score combines Contactability (35%), Business Longevity (25%), Digital Presence (20%), and Customer Review Stability (20%)."
              }
            }
          ]
        }
      ]
    }
  },
  "/how-hiring-signals-work": {
    title: "How Hiring Signals Work | BizNearYou Methodology",
    description: "Understand how review momentum, facility expansion, and digital service updates reveal expanding local employers.",
    canonicalUrl: "https://getbiznearyou.com/how-hiring-signals-work",
    robots: "index, follow",
    schemaJson: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "TechArticle",
          "@id": "https://getbiznearyou.com/how-hiring-signals-work#article",
          "name": "How Hiring Signals Work",
          "description": "In-depth guide to commercial growth metrics and hiring likelihood indicators.",
          "inLanguage": "en"
        }
      ]
    }
  },
  "/why-businesses-before-job-listings": {
    title: "Why Businesses Appear Before Job Listings | BizNearYou",
    description: "Why local small business hiring often happens through direct contact rather than public job boards, and how mapping businesses unlocks unadvertised roles.",
    canonicalUrl: "https://getbiznearyou.com/why-businesses-before-job-listings",
    robots: "index, follow",
    schemaJson: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Article",
          "@id": "https://getbiznearyou.com/why-businesses-before-job-listings#article",
          "name": "Why Businesses Appear Before Job Listings",
          "description": "Explaining the hidden job market and direct outreach advantage."
        }
      ]
    }
  },
  "/about": {
    title: "About BizNearYou",
    description: "Learn why BizNearYou was created and how it helps job seekers discover and organize local employment opportunities.",
    canonicalUrl: "https://getbiznearyou.com/about",
    robots: "index, follow",
    schemaJson: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "AboutPage",
          "@id": "https://getbiznearyou.com/about",
          "url": "https://getbiznearyou.com/about",
          "name": "About BizNearYou",
          "description": "Learn why BizNearYou was created and how it helps job seekers discover and organize local employment opportunities."
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://getbiznearyou.com/about#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://getbiznearyou.com/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "About",
              "item": "https://getbiznearyou.com/about"
            }
          ]
        }
      ]
    }
  },
  "/contact": {
    title: "Contact BizNearYou",
    description: "Contact BizNearYou for product support, questions, feedback, partnerships, or assistance with your account.",
    canonicalUrl: "https://getbiznearyou.com/contact",
    robots: "index, follow",
    schemaJson: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "ContactPage",
          "@id": "https://getbiznearyou.com/contact",
          "url": "https://getbiznearyou.com/contact",
          "name": "Contact BizNearYou",
          "description": "Contact BizNearYou for product support, questions, feedback, partnerships, or assistance with your account."
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://getbiznearyou.com/contact#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://getbiznearyou.com/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Contact",
              "item": "https://getbiznearyou.com/contact"
            }
          ]
        }
      ]
    }
  },
  "/privacy": {
    title: "Privacy Policy | BizNearYou",
    description: "Read how BizNearYou collects, uses, stores, and protects information associated with the website and its services.",
    canonicalUrl: "https://getbiznearyou.com/privacy",
    robots: "index, follow",
    schemaJson: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Privacy Policy",
      "description": "Read how BizNearYou collects, uses, stores, and protects information associated with the website and its services.",
      "url": "https://getbiznearyou.com/privacy"
    }
  },
  "/terms": {
    title: "Terms of Service | BizNearYou",
    description: "Review the terms and conditions that apply when accessing or using BizNearYou.",
    canonicalUrl: "https://getbiznearyou.com/terms",
    robots: "index, follow",
    schemaJson: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Terms of Service",
      "description": "Review the terms and conditions that apply when accessing or using BizNearYou.",
      "url": "https://getbiznearyou.com/terms"
    }
  },
  "/login": {
    title: "Log In to BizNearYou",
    description: "Access your BizNearYou CRM workspace, saved businesses, active pipelines, and custom outreach templates.",
    canonicalUrl: "https://getbiznearyou.com/login",
    robots: "noindex, follow"
  },
  "/signup": {
    title: "Sign Up for BizNearYou",
    description: "Create your free BizNearYou workspace, scout unlisted local jobs, track businesses near you, and organize direct outreach.",
    canonicalUrl: "https://getbiznearyou.com/signup",
    robots: "noindex, follow"
  },
  "/jobs/chicago-il": {
    title: "Jobs in Chicago, IL | Discover Nearby Employers | BizNearYou",
    description: "Discover nearby employers, analyze commercial activity signals, and organize direct job outreach to local businesses in Chicago, IL.",
    canonicalUrl: "https://getbiznearyou.com/jobs/chicago-il",
    robots: "index, follow"
  },
  "/jobs/austin-tx": {
    title: "Jobs in Austin, TX | Discover Nearby Employers | BizNearYou",
    description: "Discover nearby employers, analyze commercial activity signals, and organize direct job outreach to local businesses in Austin, TX.",
    canonicalUrl: "https://getbiznearyou.com/jobs/austin-tx",
    robots: "index, follow"
  },
  "/jobs/dallas-tx": {
    title: "Jobs in Dallas, TX | Discover Nearby Employers | BizNearYou",
    description: "Discover nearby employers, analyze commercial activity signals, and organize direct job outreach to local businesses in Dallas, TX.",
    canonicalUrl: "https://getbiznearyou.com/jobs/dallas-tx",
    robots: "index, follow"
  },
  "/jobs/phoenix-az": {
    title: "Jobs in Phoenix, AZ | Discover Nearby Employers | BizNearYou",
    description: "Discover nearby employers, analyze commercial activity signals, and organize direct job outreach to local businesses in Phoenix, AZ.",
    canonicalUrl: "https://getbiznearyou.com/jobs/phoenix-az",
    robots: "index, follow"
  },
  "/jobs/miami-fl": {
    title: "Jobs in Miami, FL | Discover Nearby Employers | BizNearYou",
    description: "Discover nearby employers, analyze commercial activity signals, and organize direct job outreach to local businesses in Miami, FL.",
    canonicalUrl: "https://getbiznearyou.com/jobs/miami-fl",
    robots: "index, follow"
  },
  "/jobs/peoria-il": {
    title: "Jobs in Peoria, IL | Discover Nearby Employers | BizNearYou",
    description: "Discover nearby employers, medical practices, engineering suppliers, and local businesses in Peoria, IL. Find unadvertised jobs, analyze commercial activity signals, and pitch owners directly.",
    canonicalUrl: "https://getbiznearyou.com/jobs/peoria-il",
    robots: "index, follow",
    schemaJson: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://getbiznearyou.com/jobs/peoria-il#webpage",
          "url": "https://getbiznearyou.com/jobs/peoria-il",
          "name": "Discover Local Job Opportunities in Peoria, Illinois",
          "description": "Discover nearby employers, medical practices, engineering suppliers, and local businesses in Peoria, IL."
        },
        {
          "@type": "Place",
          "name": "Peoria, Illinois",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Peoria",
            "addressRegion": "IL",
            "addressCountry": "US"
          }
        },
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://getbiznearyou.com/" },
            { "@type": "ListItem", "position": 2, "name": "Find Jobs", "item": "https://getbiznearyou.com/find-jobs" },
            { "@type": "ListItem", "position": 3, "name": "Peoria, IL Jobs", "item": "https://getbiznearyou.com/jobs/peoria-il" }
          ]
        }
      ]
    }
  },
  "/businesses-hiring/peoria-il": {
    title: "Businesses Hiring in Peoria, IL | Explore Local Signals | BizNearYou",
    description: "Track hiring signals and commercial growth in Peoria, IL. Spot expanding medical offices, bustling trade contractors, and growing law firms before job postings appear.",
    canonicalUrl: "https://getbiznearyou.com/businesses-hiring/peoria-il",
    robots: "index, follow",
    schemaJson: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://getbiznearyou.com/businesses-hiring/peoria-il#webpage",
          "url": "https://getbiznearyou.com/businesses-hiring/peoria-il",
          "name": "Find Peoria Businesses Showing Possible Hiring Signals",
          "description": "Track commercial activity signals, customer review momentum, and hiring growth in Peoria, IL."
        },
        {
          "@type": "Place",
          "name": "Peoria, Illinois",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Peoria",
            "addressRegion": "IL",
            "addressCountry": "US"
          }
        },
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://getbiznearyou.com/" },
            { "@type": "ListItem", "position": 2, "name": "Find Jobs", "item": "https://getbiznearyou.com/find-jobs" },
            { "@type": "ListItem", "position": 3, "name": "Peoria Businesses Hiring", "item": "https://getbiznearyou.com/businesses-hiring/peoria-il" }
          ]
        }
      ]
    }
  },
  "/internships/peoria-il": {
    title: "Internships in Peoria, IL | Find Nearby Experience | BizNearYou",
    description: "Find summer and semester internships in Peoria, IL. Pitch private clinics, law offices, and local businesses for unlisted student intern opportunities.",
    canonicalUrl: "https://getbiznearyou.com/internships/peoria-il",
    robots: "index, follow",
    schemaJson: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://getbiznearyou.com/internships/peoria-il#webpage",
          "url": "https://getbiznearyou.com/internships/peoria-il",
          "name": "Explore Internship and Work-Experience Opportunities in Peoria",
          "description": "Find unlisted summer and semester internships with local Peoria employers."
        },
        {
          "@type": "Place",
          "name": "Peoria, Illinois",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Peoria",
            "addressRegion": "IL",
            "addressCountry": "US"
          }
        },
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://getbiznearyou.com/" },
            { "@type": "ListItem", "position": 2, "name": "Find Jobs", "item": "https://getbiznearyou.com/find-jobs" },
            { "@type": "ListItem", "position": 3, "name": "Peoria Internships", "item": "https://getbiznearyou.com/internships/peoria-il" }
          ]
        }
      ]
    }
  }
};

function attachCentralBreadcrumbs(targetPath: string, meta: SeoPageMetadata): SeoPageMetadata {
  const breadcrumbSchema = generateBreadcrumbJsonLd(targetPath);
  if (!breadcrumbSchema) {
    if (meta.schemaJson) {
      const sj = meta.schemaJson as any;
      if (Array.isArray(sj["@graph"])) {
        const filteredGraph = sj["@graph"].filter((item: any) => item["@type"] !== "BreadcrumbList");
        return {
          ...meta,
          schemaJson: {
            ...sj,
            "@graph": filteredGraph
          }
        };
      } else if (sj["@type"] === "BreadcrumbList") {
        const { schemaJson, ...rest } = meta;
        return rest;
      }
    }
    return meta;
  }

  if (meta.schemaJson) {
    const sj = meta.schemaJson as any;
    if (Array.isArray(sj["@graph"])) {
      const filteredGraph = sj["@graph"].filter((item: any) => item["@type"] !== "BreadcrumbList");
      filteredGraph.push(breadcrumbSchema);
      return {
        ...meta,
        schemaJson: {
          ...sj,
          "@graph": filteredGraph
        }
      };
    } else if (sj["@type"] === "BreadcrumbList") {
      return {
        ...meta,
        schemaJson: breadcrumbSchema
      };
    } else {
      return {
        ...meta,
        schemaJson: {
          "@context": "https://schema.org",
          "@graph": [sj, breadcrumbSchema]
        }
      };
    }
  }

  return {
    ...meta,
    schemaJson: breadcrumbSchema
  };
}

export function getSeoConfig(path: string): SeoPageMetadata {
  const { routeWithoutLang: cleanPath } = parseRouteLanguage(path || "/");
  if (!cleanPath || cleanPath.startsWith("/node_modules/") || cleanPath.startsWith("/.vite/") || cleanPath.startsWith("/@vite/") || cleanPath.startsWith("/src/")) {
    return {
      title: "Page Not Found | BizNearYou",
      description: "Resource not available.",
      canonicalUrl: "https://getbiznearyou.com/",
      robots: "noindex, nofollow"
    };
  }

  const redirectMatch = getStandardizedLocationRedirect(cleanPath);
  const targetPath = redirectMatch ? redirectMatch.targetPath : cleanPath;
  const rawMeta = getRawSeoConfig(targetPath);
  const eligibility = isSeoIndexEligible(targetPath);

  const finalMeta: SeoPageMetadata = {
    ...rawMeta,
    title: sanitizeDuplicateWords(rawMeta.title),
    description: sanitizeDuplicateWords(rawMeta.description),
    ogTitle: rawMeta.ogTitle ? sanitizeDuplicateWords(rawMeta.ogTitle) : undefined,
    ogDescription: rawMeta.ogDescription ? sanitizeDuplicateWords(rawMeta.ogDescription) : undefined,
    robots: eligibility.isEligible ? (rawMeta.robots || "index, follow") : eligibility.indexingStatus
  };

  return attachCentralBreadcrumbs(targetPath, finalMeta);
}

export function getRawSeoConfig(targetPath: string): SeoPageMetadata {
  // Check centralized GLOBAL_SEO_REGISTRY first
  const regEntry = getSeoEntryByRoute(targetPath);
  if (regEntry) {
    let schemaObj: object;
    if (regEntry.structuredDataType === "Organization") {
      schemaObj = getOrganizationSchema();
    } else if (regEntry.structuredDataType === "WebSite") {
      schemaObj = getWebSiteSchema();
    } else if (regEntry.structuredDataType === "SoftwareApplication") {
      schemaObj = getSoftwareApplicationSchema(regEntry.title, regEntry.metaDescription);
    } else {
      schemaObj = {
        "@context": "https://schema.org",
        "@type": regEntry.structuredDataType,
        "name": regEntry.title,
        "description": regEntry.metaDescription,
        "url": regEntry.canonicalUrl
      };
    }

    return {
      title: regEntry.title,
      description: regEntry.metaDescription,
      canonicalUrl: regEntry.canonicalUrl,
      robots: regEntry.indexingStatus,
      schemaJson: schemaObj
    };
  }

  // Exact static match
  if (seoConfig[targetPath]) {
    return seoConfig[targetPath];
  }

  // Location Directory Pages
  if (targetPath === "/locations") {
    return {
      title: "City & Regional Directory of Local Employers | BizNearYou",
      description: "Explore verified local businesses, commercial hubs, and city employer directories across all supported regions.",
      canonicalUrl: "https://getbiznearyou.com/locations",
      robots: "index, follow"
    };
  }
  if (targetPath === "/jobs/locations") {
    return {
      title: "Browse Local Job Search Locations | BizNearYou",
      description: "Explore supported cities and regional hubs across the US to discover local employers, commercial activity signals, and unadvertised job opportunities.",
      canonicalUrl: "https://getbiznearyou.com/jobs/locations",
      robots: "index, follow"
    };
  }
  if (targetPath === "/businesses-hiring/locations") {
    return {
      title: "Browse Cities with Hiring Signal Directory | BizNearYou",
      description: "Find cities with active commercial growth, expanding medical offices, bustling contractors, and hiring signals worth investigating.",
      canonicalUrl: "https://getbiznearyou.com/businesses-hiring/locations",
      robots: "index, follow"
    };
  }
  if (targetPath === "/internships/locations") {
    return {
      title: "Browse Student Internship Locations Directory | BizNearYou",
      description: "Discover cities and communities with private practices, clinics, law offices, and local businesses worth pitching for student internships.",
      canonicalUrl: "https://getbiznearyou.com/internships/locations",
      robots: "index, follow"
    };
  }

  if (targetPath.startsWith("/locations/")) {
    const rawSlug = targetPath.replace("/locations/", "");
    const countryRecord = getCountryBySlug(rawSlug);
    if (countryRecord) {
      return {
        title: `${countryRecord.countryName} Local Business & City Directory | BizNearYou`,
        description: `Explore verified local businesses, commercial hubs, and city employer directories in ${countryRecord.countryName}.`,
        canonicalUrl: `https://getbiznearyou.com/locations/${countryRecord.countrySlug}`,
        robots: "index, follow"
      };
    }
  }

  // Dynamic location routes handling with central Location Registry
  if (targetPath.startsWith("/jobs/")) {
    const rawPath = targetPath.replace("/jobs/", "");
    const parts = rawPath.split("/").filter(Boolean);

    // Case A: Tier 3 Niche Intent Route e.g. /jobs/jackson-ms/part-time or /jobs/mississippi/jackson-ms/part-time
    if (parts.length >= 2) {
      const lastPart = parts[parts.length - 1];
      const nicheIntent = getNicheIntentBySlug(lastPart);
      if (nicheIntent) {
        const cityPart = parts[parts.length - 2];
        const loc = getLocationBySlug(cityPart);
        if (loc && isLocationQualified(loc)) {
          const meta = generateTier3NicheMetadata(loc, nicheIntent, "jobs");
          return {
            title: meta.title,
            description: meta.metaDescription,
            canonicalUrl: meta.canonicalUrl,
            robots: "index, follow",
            schemaJson: {
              "@context": "https://schema.org",
              "@type": "ItemList",
              "name": meta.h1,
              "description": meta.metaDescription,
              "url": meta.canonicalUrl
            }
          };
        }
      }
    }

    // Case B: Tier 2 City or Tier 1 State Route
    const candidateSlug = parts[parts.length - 1] || rawPath;
    const loc = getLocationBySlug(candidateSlug);

    if (loc && isLocationQualified(loc)) {
      const meta = generateTier2CityMetadata(loc, "jobs");
      return {
        title: meta.title,
        description: meta.metaDescription,
        canonicalUrl: meta.canonicalUrl,
        robots: "index, follow",
        schemaJson: {
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "WebPage",
              "@id": `${meta.canonicalUrl}#webpage`,
              "url": meta.canonicalUrl,
              "name": meta.h1,
              "description": meta.metaDescription
            },
            {
              "@type": "Place",
              "name": `${loc.cityName}, ${loc.stateName}`,
              "address": {
                "@type": "PostalAddress",
                "addressLocality": loc.cityName,
                "addressRegion": loc.stateAbbreviation,
                "addressCountry": loc.countryCode || "US"
              }
            }
          ]
        }
      };
    }

    const stateRecord = getStateBySlug(candidateSlug);
    if (stateRecord) {
      const stateSlug = stateRecord.stateName.toLowerCase().replace(/\s+/g, "-");
      const meta = generateTier1StateMetadata(stateRecord.stateName, stateSlug, "jobs");
      return {
        title: meta.title,
        description: meta.metaDescription,
        canonicalUrl: meta.canonicalUrl,
        robots: "index, follow"
      };
    }

    const countryRecord = getCountryBySlug(candidateSlug.replace(/^locations\//, ""));
    if (countryRecord) {
      return {
        title: `${countryRecord.countryName} Local Jobs & Hiring Directory | BizNearYou`,
        description: `Discover local job opportunities, hiring signals, and commercial growth across major cities in ${countryRecord.countryName}.`,
        canonicalUrl: `https://getbiznearyou.com/jobs/locations/${countryRecord.countrySlug}`,
        robots: "index, follow"
      };
    }

    // Fallback unknown -> noindex
    const slug = normalizeCitySlug(candidateSlug);
    return {
      title: "Location Directory | BizNearYou",
      description: "Search supported cities and regional hubs across the US to discover nearby employers and hiring signals.",
      canonicalUrl: `https://getbiznearyou.com/jobs/${slug}`,
      robots: "noindex, follow"
    };
  }

  if (targetPath.startsWith("/businesses-hiring/")) {
    const rawSlug = targetPath.replace("/businesses-hiring/", "");
    const cleanSlug = rawSlug.replace(/^locations\//, "");
    const loc = getLocationBySlug(rawSlug) || getLocationBySlug(cleanSlug);

    if (rawSlug.startsWith("locations/")) {
      const countryRecord = getCountryBySlug(cleanSlug);
      if (countryRecord) {
        return {
          title: `${countryRecord.countryName} Businesses Hiring Directory | BizNearYou`,
          description: `Track commercial growth signals, local business expansions, and customer review momentum across ${countryRecord.countryName}.`,
          canonicalUrl: `https://getbiznearyou.com/businesses-hiring/locations/${countryRecord.countrySlug}`,
          robots: "index, follow"
        };
      }
    }

    const stateRecord = getStateBySlug(rawSlug);
    if (stateRecord) {
      const stateSlug = stateRecord.regionSlug || stateRecord.stateName.toLowerCase().replace(/\s+/g, "-");
      const stateMeta = generateTier1StateMetadata(stateRecord.stateName, stateSlug, "businesses-hiring");
      return {
        title: stateMeta.title,
        description: stateMeta.metaDescription,
        canonicalUrl: stateMeta.canonicalUrl,
        robots: "index, follow"
      };
    }

    if (loc && isLocationQualified(loc)) {
      const city = loc.cityName;
      const stateAbbr = loc.stateAbbreviation;
      const stateName = loc.stateName;
      const slug = loc.citySlug;

      return {
        title: `Businesses Hiring in ${city}, ${stateAbbr} | Explore Local Signals | BizNearYou`,
        description: `Track hiring signals and commercial growth in ${city}, ${stateAbbr}. Spot expanding medical offices, bustling trade contractors, and growing law firms before job postings appear.`,
        canonicalUrl: `https://getbiznearyou.com/businesses-hiring/${slug}`,
        robots: "index, follow",
        schemaJson: {
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "WebPage",
              "@id": `https://getbiznearyou.com/businesses-hiring/${slug}#webpage`,
              "url": `https://getbiznearyou.com/businesses-hiring/${slug}`,
              "name": `Find ${city} Businesses Showing Possible Hiring Signals`,
              "description": `Track commercial activity signals, customer review momentum, and hiring growth in ${city}, ${stateAbbr}.`
            },
            {
              "@type": "Place",
              "name": `${city}, ${stateName}`,
              "address": {
                "@type": "PostalAddress",
                "addressLocality": city,
                "addressRegion": stateAbbr,
                "addressCountry": "US"
              }
            },
            {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://getbiznearyou.com/" },
                { "@type": "ListItem", "position": 2, "name": "Find Jobs", "item": "https://getbiznearyou.com/find-jobs" },
                { "@type": "ListItem", "position": 3, "name": `${city} Businesses Hiring`, "item": `https://getbiznearyou.com/businesses-hiring/${slug}` }
              ]
            }
          ]
        }
      };
    } else {
      const slug = normalizeCitySlug(rawSlug);
      return {
        title: "Location Not Found | BizNearYou",
        description: "Search supported cities and regional hubs across the US to discover nearby employers and hiring signals.",
        canonicalUrl: `https://getbiznearyou.com/businesses-hiring/${slug}`,
        robots: "noindex, follow"
      };
    }
  }

  if (targetPath.startsWith("/internships/")) {
    const rawSlug = targetPath.replace("/internships/", "");
    const cleanSlug = rawSlug.replace(/^locations\//, "");
    const loc = getLocationBySlug(rawSlug) || getLocationBySlug(cleanSlug);

    if (rawSlug.startsWith("locations/")) {
      const countryRecord = getCountryBySlug(cleanSlug);
      if (countryRecord) {
        return {
          title: `${countryRecord.countryName} Student Internships Directory | BizNearYou`,
          description: `Find student internship opportunities and unlisted position contacts across local businesses in ${countryRecord.countryName}.`,
          canonicalUrl: `https://getbiznearyou.com/internships/locations/${countryRecord.countrySlug}`,
          robots: "index, follow"
        };
      }
    }

    const stateRecord = getStateBySlug(rawSlug);
    if (stateRecord) {
      const stateSlug = stateRecord.regionSlug || stateRecord.stateName.toLowerCase().replace(/\s+/g, "-");
      const stateMeta = generateTier1StateMetadata(stateRecord.stateName, stateSlug, "internships");
      return {
        title: stateMeta.title,
        description: stateMeta.metaDescription,
        canonicalUrl: stateMeta.canonicalUrl,
        robots: "index, follow"
      };
    }

    if (loc && isLocationQualified(loc)) {
      const city = loc.cityName;
      const stateAbbr = loc.stateAbbreviation;
      const stateName = loc.stateName;
      const slug = loc.citySlug;

      return {
        title: `Internships in ${city}, ${stateAbbr} | Find Nearby Experience | BizNearYou`,
        description: `Find unlisted summer and semester internships in ${city}, ${stateAbbr}. Pitch private clinics, law offices, and local businesses for unlisted student intern opportunities.`,
        canonicalUrl: `https://getbiznearyou.com/internships/${slug}`,
        robots: "index, follow",
        schemaJson: {
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "WebPage",
              "@id": `https://getbiznearyou.com/internships/${slug}#webpage`,
              "url": `https://getbiznearyou.com/internships/${slug}`,
              "name": `Explore Internship and Work-Experience Opportunities in ${city}`,
              "description": `Find unlisted summer and semester internships with local ${city} employers.`
            },
            {
              "@type": "Place",
              "name": `${city}, ${stateName}`,
              "address": {
                "@type": "PostalAddress",
                "addressLocality": city,
                "addressRegion": stateAbbr,
                "addressCountry": "US"
              }
            },
            {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://getbiznearyou.com/" },
                { "@type": "ListItem", "position": 2, "name": "Find Jobs", "item": "https://getbiznearyou.com/find-jobs" },
                { "@type": "ListItem", "position": 3, "name": `${city} Internships`, "item": `https://getbiznearyou.com/internships/${slug}` }
              ]
            }
          ]
        }
      };
    } else {
      const slug = normalizeCitySlug(rawSlug);
      return {
        title: "Location Not Found | BizNearYou",
        description: "Search supported cities and regional hubs across the US to discover nearby employers and hiring signals.",
        canonicalUrl: `https://getbiznearyou.com/internships/${slug}`,
        robots: "noindex, follow"
      };
    }
  }

  if (targetPath.startsWith("/business/") || targetPath.includes("sharedBusinessId=")) {
    const match = targetPath.match(/sharedBusinessId=([^&]+)/);
    const rawId = match ? match[1] : targetPath.replace("/business/", "");
    const businessId = rawId ? decodeURIComponent(rawId) : "";
    return {
      title: "Shared Business Profile | BizNearYou",
      description: "View shared business profile details, contact information, and local intelligence on BizNearYou.",
      canonicalUrl: `https://getbiznearyou.com/?sharedBusinessId=${encodeURIComponent(businessId)}`,
      robots: "noindex, follow"
    };
  }

  // Fallback default
  const isPrivate = ["/dashboard", "/app", "/settings", "/saved", "/account", "/login", "/signup", "/billing"].some(p => targetPath.startsWith(p));
  return {
    title: "Find Local Jobs Before They’re Posted | BizNearYou",
    description: "Discover nearby businesses worth contacting for jobs and internships. Compare public hiring signals, save opportunities, and organize your outreach.",
    canonicalUrl: `https://getbiznearyou.com${targetPath}`,
    robots: isPrivate ? "noindex, nofollow" : "index, follow"
  };
}

